// src/pages/Permisos/PermisosHome.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Box, Paper, Typography, TextField, Button, IconButton, Tooltip, Tabs, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Checkbox, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert, CircularProgress, List, ListItemButton, ListItemIcon,
    ListItemText, Collapse, InputAdornment, Divider, Menu, MenuItem, Chip, Select,
    FormControl, InputLabel, Skeleton, Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InfoIcon from '@mui/icons-material/Info';
import KeyIcon from '@mui/icons-material/Key';
import { usePermisos } from '../../utils/usePermisos';
import { nodeApiPath } from '../../utils/paths';
import {
    permisosArbolJWT, permisosBuscarJWT, permisosStatsJWT,
    permisosModulosJWT, permisosFuncionesJWT,
    permisosObtenerUsuarioJWT, permisosEditarUsuarioJWT, permisosCambiarPasswordJWT,
    permisosUsuarioPermisosJWT, permisosGuardarPermisosJWT,
    permisosCrearGrupoJWT, permisosEditarGrupoJWT, permisosEliminarGrupoJWT,
    permisosCrearSubGrupoJWT, permisosEditarSubGrupoJWT, permisosEliminarSubGrupoJWT,
    permisosCrearUsuarioJWT, permisosAsignarMasivoJWT,
    permisosCopiarPermisosJWT, permisosContarJWT, permisosExportarJWT
} from '../../api/endpoints';

const PRIMARY = '#b71c1c';
const PRIMARY_LIGHT = '#fbe9e7';
const PRIMARY_DARK = '#7f0000';
const BORDER_RADIUS = '4px';

const titleCase = (str) => {
    if (!str) return '';
    if (str.includes('.') || str.length <= 5) return str;
    if (str === str.toUpperCase() && str.length > 5) {
        return str.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase());
    }
    return str;
};

// ─── Snackbar helper ───
function SnackbarNotif({ open, msg, severity, onClose }) {
    return (
        <Snackbar open={open} autoHideDuration={4000} onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert onClose={onClose} severity={severity} variant="filled"
                sx={{ borderRadius: BORDER_RADIUS, width: '100%' }}>
                {msg}
            </Alert>
        </Snackbar>
    );
}

// ─── Dialog with colored header ───
function ColorDialog({ open, onClose, title, children, actions, maxWidth = 'sm', icon }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth
            PaperProps={{ sx: { borderRadius: BORDER_RADIUS, overflow: 'hidden' } }}>
            <DialogTitle sx={{
                bgcolor: PRIMARY, color: '#fff', display: 'flex', alignItems: 'center', gap: 1,
                py: 1.5, px: 2
            }}>
                {icon}
                <span style={{ fontWeight: 600, color: '#fff', flex: 1, fontSize: '1rem' }}>
                    {title}
                </span>
                <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '20px !important', pb: 2 }}>
                {children}
            </DialogContent>
            {actions && <DialogActions sx={{ px: 2, pb: 2 }}>{actions}</DialogActions>}
        </Dialog>
    );
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function PermisosHome() {
    const { tienePermiso } = usePermisos();
    const puedeAcceder = tienePermiso('ADMINISTRACION DE PERMISOS', 'SELECT');

    // ── Tree state ──
    const [arbol, setArbol] = useState([]);
    const [loadingTree, setLoadingTree] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [expandedSubGroups, setExpandedSubGroups] = useState({});
    const [stats, setStats] = useState({ usuarios_activos: 0, grupos: 0 });

    // ── Selected user ──
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedUserName, setSelectedUserName] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    // ── Permisos tab ──
    const [modulos, setModulos] = useState([]);
    const [funciones, setFunciones] = useState([]);
    const [selectedModuloId, setSelectedModuloId] = useState('');
    const [userPermisos, setUserPermisos] = useState([]);
    const [permisosMatrix, setPermisosMatrix] = useState({});
    const [loadingPermisos, setLoadingPermisos] = useState(false);
    const [savingPermisos, setSavingPermisos] = useState(false);

    // ── Datos tab ──
    const [editUserData, setEditUserData] = useState(null);
    const [loadingUser, setLoadingUser] = useState(false);
    const [savingUser, setSavingUser] = useState(false);

    // ── Password tab ──
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [savingPassword, setSavingPassword] = useState(false);

    // ── Snackbar ──
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    // Mejoras: filtro, badges, copiar, exportar
    const [showInactivos, setShowInactivos] = useState(false);
    const [permisosCount, setPermisosCount] = useState({});  // { userId: modulosCount }
    const [copiarDialog, setCopiarDialog] = useState({ open: false });
    const [copiarOrigen, setCopiarOrigen] = useState('');
    const [copiarSearchResults, setCopiarSearchResults] = useState([]);
    const copiarSearchTimer = React.useRef(null);
    const notify = useCallback((msg, severity = 'success') => {
        setSnack({ open: true, msg, severity });
    }, []);

    // ── Context menu ──
    const [contextMenu, setContextMenu] = useState(null);
    const [contextTarget, setContextTarget] = useState(null);

    // ── Dialogs ──
    const [dialogGrupo, setDialogGrupo] = useState({ open: false, mode: 'create', id: null, nombre: '', parentId: null });
    const [dialogSubGrupo, setDialogSubGrupo] = useState({ open: false, mode: 'create', id: null, nombre: '', idGrupo: null });
    const [dialogDeleteGrupo, setDialogDeleteGrupo] = useState({ open: false, type: '', id: null, nombre: '' });
    const [dialogUsuario, setDialogUsuario] = useState({ open: false, idSubGrupo: null, subGrupoNombre: '' });
    const [nuevoUsuario, setNuevoUsuario] = useState({ usuario: '', nombre: '', descripcion: '', password: '' });
    const [dialogMasivo, setDialogMasivo] = useState({ open: false, idSubGrupo: null, subGrupoNombre: '' });
    const [masivoModuloId, setMasivoModuloId] = useState('');
    const [masivoMatrix, setMasivoMatrix] = useState({});

    const searchTimeout = useRef(null);

    // ═══════════════════════════════════════════════════════
    // LOAD INITIAL DATA
    // ═══════════════════════════════════════════════════════
    const loadArbol = useCallback(async () => {
        setLoadingTree(true);
        try {
            const data = await permisosArbolJWT();
            // Normalizar nombres de campos del backend
            const normalizado = (data?.arbol || []).map(g => ({
                ...g,
                id: g.Id, nombre: g.Grupo,
                subgrupos: (g.subgrupos || []).map(sg => ({
                    ...sg,
                    id: sg.Id, nombre: sg.SubGrupo, idGrupo: sg.Id_Grupo,
                    usuarios: (sg.usuarios || []).map(u => ({
                        ...u,
                        id: u.Id, nombre: u.Apellido_Nombre, activo: u.Activo,
                        idSubGrupo: u.Id_SubGrupo, descripcion: u.Descripcion,
                    }))
                }))
            }));
            setArbol(normalizado);
        } catch (e) {
            notify('Error al cargar el arbol de usuarios', 'error');
        } finally {
            setLoadingTree(false);
        }
    }, [notify]);

    const loadStats = useCallback(async () => {
        try {
            const data = await permisosStatsJWT();
            if (data?.ok) setStats(data);
        } catch { }
    }, []);

    const loadModulosYFunciones = useCallback(async () => {
        try {
            const modsR = await permisosModulosJWT();
            setModulos((modsR?.modulos || []).map(m => ({ id: m.Id, nombre: m.Modulo })));
        } catch (e) {
            notify('Error al cargar modulos', 'error');
        }
    }, [notify]);

    // Cargar funciones cuando cambia el módulo seleccionado
    useEffect(() => {
        if (!selectedModuloId) { setFunciones([]); return; }
        permisosFuncionesJWT(selectedModuloId).then(r => {
            setFunciones((r?.funciones || []).map(f => ({ id: f.Id, nombre: f.Funcion })));
        }).catch(() => setFunciones([]));
    }, [selectedModuloId]);

    useEffect(() => {
        if (!puedeAcceder) return;
        loadArbol();
        loadStats();
        loadModulosYFunciones();
        // Cargar conteo de permisos para badges
        permisosContarJWT().then(r => {
            if (r?.ok) {
                const map = {};
                (r.conteo || []).forEach(c => { map[c.Id_Usuario] = c.modulos_con_permiso; });
                setPermisosCount(map);
            }
        }).catch(() => {});
    }, [puedeAcceder, loadArbol, loadStats, loadModulosYFunciones]);

    // ═══════════════════════════════════════════════════════
    // SEARCH
    // ═══════════════════════════════════════════════════════
    const handleSearch = useCallback((q) => {
        setSearchQuery(q);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!q || q.length < 2) {
            setSearchResults(null);
            return;
        }
        setSearchLoading(true);
        searchTimeout.current = setTimeout(async () => {
            try {
                const data = await permisosBuscarJWT(q);
                setSearchResults(data?.usuarios || []);
            } catch {
                setSearchResults([]);
            } finally {
                setSearchLoading(false);
            }
        }, 400);
    }, []);

    // ═══════════════════════════════════════════════════════
    // SELECT USER
    // ═══════════════════════════════════════════════════════
    const selectUser = useCallback((id, nombre) => {
        setSelectedUserId(id);
        setSelectedUserName(nombre || '');
        setActiveTab(0);
        // userData cleared
        setEditUserData(null);
        setUserPermisos([]);
        setPermisosMatrix({});
        setSelectedModuloId('');
        setNewPassword('');
        setConfirmPassword('');
    }, []);

    // ═══════════════════════════════════════════════════════
    // TAB 1: PERMISOS
    // ═══════════════════════════════════════════════════════
    const loadUserPermisos = useCallback(async () => {
        if (!selectedUserId) return;
        setLoadingPermisos(true);
        try {
            const data = await permisosUsuarioPermisosJWT(selectedUserId);
            setUserPermisos((data?.permisos || []).map(p => ({
                id: p.Id, idFuncion: p.Id_Funcion, nombre: p.Funcion,
                idModulo: p.Id_Modulo, modulo: p.Modulo,
                _SELECT: p._SELECT, _INSERT: p._INSERT, _UPDATE: p._UPDATE,
                _DELETE: p._DELETE, _REPORT: p._REPORT,
            })));
        } catch (e) {
            notify('Error al cargar permisos del usuario', 'error');
            setUserPermisos([]);
        } finally {
            setLoadingPermisos(false);
        }
    }, [selectedUserId, notify]);

    useEffect(() => {
        if (selectedUserId && activeTab === 1) loadUserPermisos();
    }, [selectedUserId, activeTab, loadUserPermisos]);

    // Build matrix when modulo changes
    useEffect(() => {
        if (!selectedModuloId || !funciones.length) {
            setPermisosMatrix({});
            return;
        }
        const matrix = {};
        funciones.forEach(f => {
            const fId = f.id || f.Id;
            const existing = userPermisos.find(
                p => String(p.idModulo || p.Id_Modulo) === String(selectedModuloId) && String(p.idFuncion || p.Id_Funcion) === String(fId)
            );
            matrix[fId] = {
                nombre: f.nombre || f.Nombre || f.Funcion,
                SELECT: existing ? !!(existing._SELECT || existing.SELECT) : false,
                INSERT: existing ? !!(existing._INSERT || existing.INSERT) : false,
                UPDATE: existing ? !!(existing._UPDATE || existing.UPDATE) : false,
                DELETE: existing ? !!(existing._DELETE || existing.DELETE) : false,
                REPORTES: existing ? !!(existing._REPORT || existing.REPORTES) : false,
            };
        });
        setPermisosMatrix(matrix);
    }, [selectedModuloId, funciones, userPermisos]);

    const handlePermisoChange = (idFuncion, accion) => {
        setPermisosMatrix(prev => ({
            ...prev,
            [idFuncion]: {
                ...prev[idFuncion],
                [accion]: !prev[idFuncion][accion]
            }
        }));
    };

    const handleSelectAllColumn = (accion) => {
        setPermisosMatrix(prev => {
            const allChecked = Object.values(prev).every(f => f[accion]);
            const next = {};
            for (const key in prev) {
                next[key] = { ...prev[key], [accion]: !allChecked };
            }
            return next;
        });
    };

    const savePermisos = async () => {
        if (!selectedUserId || !selectedModuloId) return;
        setSavingPermisos(true);
        try {
            const permisos = Object.entries(permisosMatrix).map(([idFuncion, perm]) => ({
                idFuncion: Number(idFuncion),
                _SELECT: perm.SELECT,
                _INSERT: perm.INSERT,
                _UPDATE: perm.UPDATE,
                _DELETE: perm.DELETE,
                _REPORT: perm.REPORTES,
            }));
            await permisosGuardarPermisosJWT(selectedUserId, Number(selectedModuloId), permisos);
            notify('Permisos guardados correctamente');
            await loadUserPermisos();
        } catch (e) {
            notify('Error al guardar permisos', 'error');
        } finally {
            setSavingPermisos(false);
        }
    };

    // ═══════════════════════════════════════════════════════
    // TAB 2: DATOS
    // ═══════════════════════════════════════════════════════
    const loadUserData = useCallback(async () => {
        if (!selectedUserId) return;
        setLoadingUser(true);
        try {
            const data = await permisosObtenerUsuarioJWT(selectedUserId);
            if (data?.ok) {
                const u = data.usuario;
                setEditUserData({
                    id: u.Id, nombre: u.Apellido_Nombre, descripcion: u.Descripcion,
                    activo: u.Activo, idSubGrupo: String(u.Id_SubGrupo || ''),
                    idPersonal: u.Id_Personal, grupo: u.Grupo, subGrupo: u.SubGrupo,
                    email: u.Email_Institucional || u.Email, telefono: u.Telefono,
                    celular: u.Celular, matricula: u.Matricula_Provincial, cuil: u.CUIL,
                });
            }
        } catch (e) {
            notify('Error al cargar datos del usuario', 'error');
        } finally {
            setLoadingUser(false);
        }
    }, [selectedUserId, notify]);

    useEffect(() => {
        if (selectedUserId && activeTab === 0) loadUserData();
    }, [selectedUserId, activeTab, loadUserData]);

    const saveUserData = async () => {
        if (!selectedUserId || !editUserData) return;
        setSavingUser(true);
        try {
            await permisosEditarUsuarioJWT(selectedUserId, {
                nombre: editUserData.nombre || editUserData.Nombre,
                descripcion: editUserData.descripcion || editUserData.Descripcion || '',
                activo: editUserData.activo !== undefined ? editUserData.activo : editUserData.Activo,
                idSubGrupo: editUserData.idSubGrupo || editUserData.Id_SubGrupo,
                idPersonal: editUserData.idPersonal || editUserData.Id_Personal || null,
            });
            notify('Datos del usuario actualizados');
            loadArbol();
            loadStats();
        } catch (e) {
            notify('Error al guardar datos del usuario', 'error');
        } finally {
            setSavingUser(false);
        }
    };

    // ═══════════════════════════════════════════════════════
    // TAB 3: PASSWORD
    // ═══════════════════════════════════════════════════════
    const savePassword = async () => {
        if (!selectedUserId) return;
        if (!newPassword) { notify('Ingrese una contraseña', 'warning'); return; }
        if (newPassword.length < 8) { notify('Mínimo 8 caracteres', 'warning'); return; }
        if (!/[A-Z]/.test(newPassword)) { notify('Debe tener al menos una mayúscula', 'warning'); return; }
        if (!/[a-z]/.test(newPassword)) { notify('Debe tener al menos una minúscula', 'warning'); return; }
        if (!/[0-9]/.test(newPassword)) { notify('Debe tener al menos un número', 'warning'); return; }
        if (newPassword !== confirmPassword) { notify('Las contraseñas no coinciden', 'warning'); return; }
        setSavingPassword(true);
        try {
            await permisosCambiarPasswordJWT(selectedUserId, newPassword);
            notify('Contraseña actualizada correctamente');
            setNewPassword('');
            setConfirmPassword('');
        } catch (e) {
            notify('Error al cambiar la contraseña', 'error');
        } finally {
            setSavingPassword(false);
        }
    };

    // ═══════════════════════════════════════════════════════
    // CONTEXT MENU (groups / subgroups)
    // ═══════════════════════════════════════════════════════
    const handleContextMenu = (event, type, item, parentId = null) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
        setContextTarget({ type, item, parentId });
    };

    const handleDotsMenu = (event, type, item, parentId = null) => {
        event.stopPropagation();
        setContextMenu({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
        setContextTarget({ type, item, parentId });
    };

    const closeContextMenu = () => {
        setContextMenu(null);
        setContextTarget(null);
    };

    // ═══════════════════════════════════════════════════════
    // GROUP CRUD
    // ═══════════════════════════════════════════════════════
    const openCreateGrupo = () => {
        closeContextMenu();
        setDialogGrupo({ open: true, mode: 'create', id: null, nombre: '' });
    };

    const openEditGrupo = () => {
        if (!contextTarget || contextTarget.type !== 'grupo') return;
        const g = contextTarget.item;
        closeContextMenu();
        setDialogGrupo({ open: true, mode: 'edit', id: g.Id || g.id, nombre: g.Nombre || g.nombre || '' });
    };

    const openDeleteGrupo = () => {
        if (!contextTarget) return;
        const item = contextTarget.item;
        const type = contextTarget.type;
        closeContextMenu();
        setDialogDeleteGrupo({
            open: true,
            type,
            id: item.Id || item.id,
            nombre: item.Nombre || item.nombre || ''
        });
    };

    const saveGrupo = async () => {
        const { mode, id, nombre } = dialogGrupo;
        if (!nombre.trim()) { notify('Ingrese un nombre', 'warning'); return; }
        try {
            if (mode === 'create') {
                await permisosCrearGrupoJWT(nombre.trim());
                notify('Grupo creado correctamente');
            } else {
                await permisosEditarGrupoJWT(id, nombre.trim());
                notify('Grupo actualizado correctamente');
            }
            setDialogGrupo({ open: false, mode: 'create', id: null, nombre: '' });
            loadArbol();
            loadStats();
        } catch (e) {
            notify('Error al guardar grupo', 'error');
        }
    };

    // ═══════════════════════════════════════════════════════
    // SUBGROUP CRUD
    // ═══════════════════════════════════════════════════════
    const openCreateSubGrupo = () => {
        if (!contextTarget || contextTarget.type !== 'grupo') return;
        const g = contextTarget.item;
        closeContextMenu();
        setDialogSubGrupo({ open: true, mode: 'create', id: null, nombre: '', idGrupo: g.Id || g.id });
    };

    const openEditSubGrupo = () => {
        if (!contextTarget || contextTarget.type !== 'subgrupo') return;
        const sg = contextTarget.item;
        closeContextMenu();
        setDialogSubGrupo({
            open: true, mode: 'edit',
            id: sg.Id || sg.id,
            nombre: sg.Nombre || sg.nombre || '',
            idGrupo: sg.Id_Grupo || sg.idGrupo || contextTarget.parentId
        });
    };

    const saveSubGrupo = async () => {
        const { mode, id, nombre, idGrupo } = dialogSubGrupo;
        if (!nombre.trim()) { notify('Ingrese un nombre', 'warning'); return; }
        try {
            if (mode === 'create') {
                await permisosCrearSubGrupoJWT(nombre.trim(), idGrupo);
                notify('Subgrupo creado correctamente');
            } else {
                await permisosEditarSubGrupoJWT(id, nombre.trim());
                notify('Subgrupo actualizado correctamente');
            }
            setDialogSubGrupo({ open: false, mode: 'create', id: null, nombre: '', idGrupo: null });
            loadArbol();
            loadStats();
        } catch (e) {
            notify('Error al guardar subgrupo', 'error');
        }
    };

    const confirmDelete = async () => {
        const { type, id } = dialogDeleteGrupo;
        try {
            if (type === 'grupo') {
                await permisosEliminarGrupoJWT(id);
                notify('Grupo eliminado');
            } else {
                await permisosEliminarSubGrupoJWT(id);
                notify('Subgrupo eliminado');
            }
            setDialogDeleteGrupo({ open: false, type: '', id: null, nombre: '' });
            loadArbol();
            loadStats();
        } catch (e) {
            notify('Error al eliminar', 'error');
        }
    };

    // ═══════════════════════════════════════════════════════
    // CREATE USER
    // ═══════════════════════════════════════════════════════
    const openCreateUsuario = () => {
        if (!contextTarget || contextTarget.type !== 'subgrupo') return;
        const sg = contextTarget.item;
        closeContextMenu();
        setDialogUsuario({ open: true, idSubGrupo: sg.Id || sg.id, subGrupoNombre: sg.Nombre || sg.nombre || '' });
        setNuevoUsuario({ usuario: '', nombre: '', descripcion: '', password: '' });
    };

    const saveNuevoUsuario = async () => {
        const { usuario, nombre, password } = nuevoUsuario;
        if (!usuario.trim() || !nombre.trim()) { notify('Complete usuario y nombre', 'warning'); return; }
        if (!password || password.length < 8) { notify('La contraseña debe tener al menos 8 caracteres', 'warning'); return; }
        if (!/[A-Z]/.test(password)) { notify('Debe tener al menos una mayúscula', 'warning'); return; }
        if (!/[a-z]/.test(password)) { notify('Debe tener al menos una minúscula', 'warning'); return; }
        if (!/[0-9]/.test(password)) { notify('Debe tener al menos un número', 'warning'); return; }
        try {
            await permisosCrearUsuarioJWT({
                usuario: usuario.trim(),
                nombre: nombre.trim(),
                descripcion: nuevoUsuario.descripcion?.trim() || '',
                password,
                idSubGrupo: dialogUsuario.idSubGrupo
            });
            notify('Usuario creado correctamente');
            setDialogUsuario({ open: false, idSubGrupo: null, subGrupoNombre: '' });
            loadArbol();
            loadStats();
        } catch (e) {
            notify(e?.message || 'Error al crear usuario', 'error');
        }
    };

    // ═══════════════════════════════════════════════════════
    // BULK ASSIGN
    // ═══════════════════════════════════════════════════════
    const openBulkAssign = () => {
        if (!contextTarget || contextTarget.type !== 'subgrupo') return;
        const sg = contextTarget.item;
        closeContextMenu();
        setDialogMasivo({ open: true, idSubGrupo: sg.Id || sg.id, subGrupoNombre: sg.Nombre || sg.nombre || '' });
        setMasivoModuloId('');
        setMasivoMatrix({});
    };

    useEffect(() => {
        if (!dialogMasivo.open || !masivoModuloId || !funciones.length) {
            if (dialogMasivo.open && !masivoModuloId) setMasivoMatrix({});
            return;
        }
        const matrix = {};
        funciones.forEach(f => {
            matrix[f.Id] = {
                nombre: f.Nombre,
                SELECT: false, INSERT: false, UPDATE: false, DELETE: false, REPORTES: false,
            };
        });
        setMasivoMatrix(matrix);
    }, [masivoModuloId, dialogMasivo.open, funciones]);

    const handleMasivoChange = (idFuncion, accion) => {
        setMasivoMatrix(prev => ({
            ...prev,
            [idFuncion]: { ...prev[idFuncion], [accion]: !prev[idFuncion][accion] }
        }));
    };

    const saveMasivo = async () => {
        if (!dialogMasivo.idSubGrupo || !masivoModuloId) return;
        try {
            const permisos = Object.entries(masivoMatrix).map(([idFuncion, perm]) => ({
                idFuncion: Number(idFuncion),
                SELECT: perm.SELECT,
                INSERT: perm.INSERT,
                UPDATE: perm.UPDATE,
                DELETE: perm.DELETE,
                REPORTES: perm.REPORTES,
            }));
            await permisosAsignarMasivoJWT(dialogMasivo.idSubGrupo, Number(masivoModuloId), permisos);
            notify('Permisos asignados masivamente');
            setDialogMasivo({ open: false, idSubGrupo: null, subGrupoNombre: '' });
            if (selectedUserId) loadUserPermisos();
        } catch (e) {
            notify('Error al asignar permisos masivamente', 'error');
        }
    };

    // ═══════════════════════════════════════════════════════
    // TREE TOGGLE
    // ═══════════════════════════════════════════════════════
    const toggleGroup = (id) => {
        setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };
    const toggleSubGroup = (id) => {
        setExpandedSubGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // ═══════════════════════════════════════════════════════
    // SUBGROUP LIST for user edit
    // ═══════════════════════════════════════════════════════
    const allSubGroups = useMemo(() => {
        const list = [];
        (arbol || []).forEach(g => {
            (g.subgrupos || g.SubGrupos || []).forEach(sg => {
                list.push({
                    id: sg.Id || sg.id,
                    nombre: `${g.Nombre || g.nombre} / ${sg.Nombre || sg.nombre}`,
                });
            });
        });
        return list;
    }, [arbol]);

    // ═══════════════════════════════════════════════════════
    // ACCESS DENIED
    // ═══════════════════════════════════════════════════════
    if (!puedeAcceder) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: BORDER_RADIUS }}>
                    <LockIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No tiene permisos para acceder a este modulo
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // ═══════════════════════════════════════════════════════
    // RENDER: LEFT PANEL - TREE
    // ═══════════════════════════════════════════════════════
    const renderTree = () => {
        const items = searchResults !== null ? searchResults : arbol;

        if (loadingTree) {
            return (
                <Box sx={{ p: 2 }}>
                    {[1, 2, 3, 4, 5].map(i => (
                        <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 1, borderRadius: BORDER_RADIUS }} />
                    ))}
                </Box>
            );
        }

        // If search results, show flat user list
        if (searchResults !== null) {
            if (searchResults.length === 0) {
                return (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Sin resultados</Typography>
                    </Box>
                );
            }
            return (
                <List dense disablePadding>
                    {searchResults.map(u => {
                        const uid = u.Id || u.id;
                        const nombre = u.Apellido_Nombre || u.nombre || u.Nombre || '';
                        const isSelected = selectedUserId === uid;
                        return (
                            <ListItemButton
                                key={uid}
                                selected={isSelected}
                                onClick={() => selectUser(uid, nombre)}
                                sx={{
                                    py: 0.5, px: 2,
                                    '&.Mui-selected': { bgcolor: PRIMARY_LIGHT, borderLeft: `3px solid ${PRIMARY}` }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <PersonIcon fontSize="small" sx={{ color: isSelected ? PRIMARY : '#999' }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={titleCase(nombre)}
                                    secondary={`${uid} - ${u.Grupo || ''} / ${u.SubGrupo || ''}`}
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 600 : 400 }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            );
        }

        // Normal tree view
        return (
            <List dense disablePadding>
                {(items || []).map(grupo => {
                    const gId = grupo.Id || grupo.id;
                    const gNombre = grupo.Nombre || grupo.nombre || '';
                    const subgrupos = grupo.subgrupos || grupo.SubGrupos || [];
                    const isExpanded = !!expandedGroups[gId];

                    return (
                        <React.Fragment key={`g-${gId}`}>
                            {/* GROUP */}
                            <ListItemButton
                                onClick={() => toggleGroup(gId)}
                                onContextMenu={(e) => handleContextMenu(e, 'grupo', grupo)}
                                sx={{
                                    py: 0.5, px: 1,
                                    '&:hover .dots-btn': { opacity: 1 }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                    {isExpanded
                                        ? <FolderOpenIcon fontSize="small" sx={{ color: PRIMARY }} />
                                        : <FolderIcon fontSize="small" sx={{ color: '#666' }} />
                                    }
                                </ListItemIcon>
                                <ListItemText
                                    primary={titleCase(gNombre)}
                                    primaryTypographyProps={{
                                        variant: 'body2', fontWeight: 600,
                                        sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    className="dots-btn"
                                    onClick={(e) => handleDotsMenu(e, 'grupo', grupo)}
                                    sx={{ opacity: 0, transition: 'opacity .2s', p: 0.25 }}
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </ListItemButton>

                            {/* SUBGROUPS */}
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <List dense disablePadding>
                                    {subgrupos.map(sg => {
                                        const sgId = sg.Id || sg.id;
                                        const sgNombre = sg.Nombre || sg.nombre || '';
                                        const todosUsuarios = sg.usuarios || sg.Usuarios || [];
                                        const usuarios = showInactivos ? todosUsuarios : todosUsuarios.filter(u => u.activo !== 0 && u.activo !== false);
                                        const isSgExpanded = !!expandedSubGroups[sgId];

                                        return (
                                            <React.Fragment key={`sg-${sgId}`}>
                                                <ListItemButton
                                                    onClick={() => toggleSubGroup(sgId)}
                                                    onContextMenu={(e) => handleContextMenu(e, 'subgrupo', sg, gId)}
                                                    sx={{
                                                        py: 0.25, pl: 4, pr: 1,
                                                        '&:hover .dots-btn': { opacity: 1 }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                        <GroupIcon fontSize="small" sx={{ color: isSgExpanded ? PRIMARY : '#999' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={titleCase(sgNombre)}
                                                        primaryTypographyProps={{
                                                            variant: 'body2',
                                                            sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                                                        }}
                                                    />
                                                    <Chip
                                                        label={usuarios.length}
                                                        size="small"
                                                        sx={{
                                                            height: 18, fontSize: '0.65rem',
                                                            bgcolor: '#eee', mr: 0.5
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        className="dots-btn"
                                                        onClick={(e) => handleDotsMenu(e, 'subgrupo', sg, gId)}
                                                        sx={{ opacity: 0, transition: 'opacity .2s', p: 0.25 }}
                                                    >
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                    {isSgExpanded ? <ExpandLess fontSize="small" sx={{ fontSize: 16 }} /> : <ExpandMore fontSize="small" sx={{ fontSize: 16 }} />}
                                                </ListItemButton>

                                                {/* USERS */}
                                                <Collapse in={isSgExpanded} timeout="auto" unmountOnExit>
                                                    <List dense disablePadding>
                                                        {usuarios.map(u => {
                                                            const uid = u.id || u.Id;
                                                            const nombre = u.nombre || u.Apellido_Nombre || u.Nombre || '';
                                                            const activo = u.activo !== undefined ? u.activo : u.Activo;
                                                            const isSelected = selectedUserId === uid;

                                                            return (
                                                                <ListItemButton
                                                                    key={`u-${uid}`}
                                                                    selected={isSelected}
                                                                    onClick={() => selectUser(uid, nombre)}
                                                                    sx={{
                                                                        py: 0.25, pl: 7, pr: 1,
                                                                        '&.Mui-selected': {
                                                                            bgcolor: PRIMARY_LIGHT,
                                                                            borderLeft: `3px solid ${PRIMARY}`
                                                                        }
                                                                    }}
                                                                >
                                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                                        {u.Id_Personal || u.idPersonal ? (
                                                                            <Box component="img"
                                                                                src={`${nodeApiPath('permisos/foto-personal/' + (u.Id_Personal || u.idPersonal))}?token=${localStorage.getItem('SIGIF_JWT_TOKEN')}`}
                                                                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'inline-flex'); }}
                                                                                sx={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }}
                                                                            />
                                                                        ) : null}
                                                                        <PersonIcon sx={{
                                                                            fontSize: 16,
                                                                            display: (u.Id_Personal || u.idPersonal) ? 'none' : 'inline-flex',
                                                                            color: isSelected ? PRIMARY : (activo === false || activo === 0) ? '#ccc' : permisosCount[uid] ? '#4CAF50' : '#ccc'
                                                                        }} />
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={<>
                                                                            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: permisosCount[uid] ? '#4CAF50' : '#ddd', marginRight: 4, verticalAlign: 'middle' }} />
                                                                            {titleCase(nombre)}
                                                                        </>}
                                                                        primaryTypographyProps={{
                                                                            variant: 'body2',
                                                                            fontWeight: isSelected ? 600 : 400,
                                                                            sx: {
                                                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                                color: (activo === false || activo === 0) ? '#bbb' : 'inherit',
                                                                                fontStyle: (activo === false || activo === 0) ? 'italic' : 'normal'
                                                                            }
                                                                        }}
                                                                    />
                                                                </ListItemButton>
                                                            );
                                                        })}
                                                    </List>
                                                </Collapse>
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    );
                })}
            </List>
        );
    };

    // ═══════════════════════════════════════════════════════
    // RENDER: PERMISSION MATRIX TABLE
    // ═══════════════════════════════════════════════════════
    const renderPermisosMatrix = (matrix, onChangeFn, readOnly = false) => {
        const acciones = ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'REPORTES'];
        const accionLabels = { SELECT: 'Ver', INSERT: 'Crear', UPDATE: 'Editar', DELETE: 'Eliminar', REPORTES: 'Reportes' };
        const entries = Object.entries(matrix);
        if (entries.length === 0) return null;

        return (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: BORDER_RADIUS, mt: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Funcion</TableCell>
                            {acciones.map(a => (
                                <TableCell key={a} align="center" sx={{ fontWeight: 600, width: 80 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{accionLabels[a]}</Typography>
                                        {!readOnly && (
                                            <Tooltip title={`Marcar/desmarcar todos - ${accionLabels[a]}`}>
                                                <Checkbox
                                                    size="small"
                                                    checked={entries.length > 0 && entries.every(([, f]) => f[a])}
                                                    indeterminate={entries.some(([, f]) => f[a]) && !entries.every(([, f]) => f[a])}
                                                    onChange={() => {
                                                        // For bulk assign dialog vs user permisos
                                                        if (onChangeFn === handlePermisoChange) {
                                                            handleSelectAllColumn(a);
                                                        } else {
                                                            // For masivo
                                                            setMasivoMatrix(prev => {
                                                                const allChecked = Object.values(prev).every(f => f[a]);
                                                                const next = {};
                                                                for (const key in prev) {
                                                                    next[key] = { ...prev[key], [a]: !allChecked };
                                                                }
                                                                return next;
                                                            });
                                                        }
                                                    }}
                                                    sx={{ p: 0, '&.Mui-checked': { color: PRIMARY }, '&.MuiCheckbox-indeterminate': { color: PRIMARY } }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.map(([idFuncion, perm]) => {
                            const allChecked = acciones.every(a => perm[a]);
                            const someChecked = acciones.some(a => perm[a]);
                            return (
                            <TableRow key={idFuncion} hover>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {!readOnly && (
                                            <Tooltip title="Todos los permisos">
                                                <Checkbox
                                                    size="small"
                                                    checked={allChecked}
                                                    indeterminate={someChecked && !allChecked}
                                                    onChange={() => {
                                                        acciones.forEach(a => {
                                                            if (allChecked) {
                                                                if (perm[a]) onChangeFn(idFuncion, a);
                                                            } else {
                                                                if (!perm[a]) onChangeFn(idFuncion, a);
                                                            }
                                                        });
                                                    }}
                                                    sx={{ p: 0.25, '&.Mui-checked': { color: PRIMARY }, '&.MuiCheckbox-indeterminate': { color: PRIMARY } }}
                                                />
                                            </Tooltip>
                                        )}
                                        <Typography variant="body2">{titleCase(perm.nombre)}</Typography>
                                    </Box>
                                </TableCell>
                                {acciones.map(a => (
                                    <TableCell key={a} align="center" sx={{ py: 0.25 }}>
                                        <Checkbox
                                            size="small"
                                            checked={!!perm[a]}
                                            disabled={readOnly}
                                            onChange={() => onChangeFn(idFuncion, a)}
                                            sx={{ p: 0.25, '&.Mui-checked': { color: PRIMARY } }}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // ═══════════════════════════════════════════════════════
    // RENDER: TAB CONTENT
    // ═══════════════════════════════════════════════════════
    const renderTabPermisos = () => (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 300, flex: 1 }}>
                    <InputLabel>Modulo</InputLabel>
                    <Select
                        value={selectedModuloId}
                        onChange={(e) => setSelectedModuloId(e.target.value)}
                        label="Modulo"
                        sx={{ borderRadius: BORDER_RADIUS }}
                    >
                        <MenuItem value=""><em>Seleccione un modulo</em></MenuItem>
                        {modulos.map(m => (
                            <MenuItem key={m.Id || m.id} value={String(m.Id || m.id)}>
                                {titleCase(m.Nombre || m.nombre)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {loadingPermisos ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} sx={{ color: PRIMARY }} />
                </Box>
            ) : selectedModuloId ? (
                <>
                    {renderPermisosMatrix(permisosMatrix, handlePermisoChange)}
                    <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={savingPermisos ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={savePermisos}
                            disabled={savingPermisos}
                            sx={{
                                bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none',
                                '&:hover': { bgcolor: PRIMARY_DARK }
                            }}
                        >
                            Guardar permisos
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={async () => {
                                if (!selectedModuloId || !editUserData?.idSubGrupo) return;
                                const ok = window.confirm('¿Copiar estos permisos a TODOS los usuarios del mismo subgrupo?');
                                if (!ok) return;
                                const permisos = Object.entries(permisosMatrix).map(([idFuncion, perm]) => ({
                                    idFuncion: Number(idFuncion),
                                    _SELECT: perm.SELECT, _INSERT: perm.INSERT, _UPDATE: perm.UPDATE,
                                    _DELETE: perm.DELETE, _REPORT: perm.REPORTES,
                                }));
                                try {
                                    const r = await permisosAsignarMasivoJWT(editUserData.idSubGrupo, Number(selectedModuloId), permisos);
                                    if (r?.ok) notify(`Permisos aplicados a ${r.afectados} usuarios`);
                                    else notify(r?.mensaje || 'Error', 'error');
                                } catch { notify('Error', 'error'); }
                            }}
                            sx={{ borderRadius: BORDER_RADIUS, textTransform: 'none', color: PRIMARY, borderColor: PRIMARY }}
                        >
                            Aplicar al subgrupo
                        </Button>
                    </Box>
                </>
            ) : (
                <Box sx={{ textAlign: 'center', py: 6, color: '#999' }}>
                    <SecurityIcon sx={{ fontSize: 48, mb: 1, color: '#ddd' }} />
                    <Typography variant="body2">Seleccione un modulo para ver/editar permisos</Typography>
                </Box>
            )}
        </Box>
    );

    // Copiar permisos: buscar usuario origen
    const handleCopiarSearch = (q) => {
        if (copiarSearchTimer.current) clearTimeout(copiarSearchTimer.current);
        if (!q || q.length < 2) { setCopiarSearchResults([]); return; }
        copiarSearchTimer.current = setTimeout(async () => {
            try {
                const r = await permisosBuscarJWT(q);
                setCopiarSearchResults((r?.usuarios || []).filter(u => u.Id !== selectedUserId));
            } catch { setCopiarSearchResults([]); }
        }, 300);
    };

    const handleCopiarPermisos = async () => {
        if (!copiarOrigen || !selectedUserId) return;
        try {
            const r = await permisosCopiarPermisosJWT(copiarOrigen, selectedUserId);
            if (r?.ok) {
                notify(`${r.copiados} permisos copiados`);
                setCopiarDialog({ open: false });
                setCopiarOrigen('');
                loadUserPermisos();
            } else {
                notify('Error al copiar', 'error');
            }
        } catch { notify('Error', 'error'); }
    };

    const handleExportarPermisos = async () => {
        if (!selectedUserId) return;
        try {
            const r = await permisosExportarJWT(selectedUserId);
            if (!r?.ok || !r.permisos?.length) { notify('No hay permisos para exportar', 'warning'); return; }
            // Generar CSV
            const headers = 'Módulo,Función,Ver,Crear,Editar,Eliminar,Reportes\n';
            const rows = r.permisos.map(p =>
                `"${p.Modulo}","${p.Funcion}",${p.Ver},${p.Crear},${p.Editar},${p.Eliminar},${p.Reportes}`
            ).join('\n');
            const blob = new Blob(['\uFEFF' + headers + rows], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `permisos_${selectedUserId}.csv`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch { notify('Error al exportar', 'error'); }
    };

    const renderTabDatos = () => {
        if (loadingUser) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} sx={{ color: PRIMARY }} />
                </Box>
            );
        }
        if (!editUserData) {
            return (
                <Box sx={{ textAlign: 'center', py: 6, color: '#999' }}>
                    <InfoIcon sx={{ fontSize: 48, mb: 1, color: '#ddd' }} />
                    <Typography variant="body2">Cargando datos del usuario...</Typography>
                </Box>
            );
        }

        const usuario = editUserData.id || editUserData.usuario || editUserData.Usuario || '';
        const nombre = editUserData.nombre || editUserData.Nombre || '';
        const descripcion = editUserData.descripcion !== undefined ? editUserData.descripcion : (editUserData.Descripcion || '');
        const activo = editUserData.activo !== undefined ? editUserData.activo : editUserData.Activo;
        const idSubGrupo = editUserData.idSubGrupo || editUserData.Id_SubGrupo || '';
        const idPersonal = editUserData.idPersonal || editUserData.Id_Personal || '';

        return (
            <Box sx={{ display: 'flex', gap: 3 }}>
                {/* Foto */}
                {idPersonal && (
                    <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                        <Box component="img"
                            src={`${nodeApiPath('permisos/foto-personal/' + idPersonal)}?token=${localStorage.getItem('SIGIF_JWT_TOKEN')}`}
                            onError={(e) => { e.target.style.display = 'none'; }}
                            sx={{ width: 100, height: 100, borderRadius: '4px', objectFit: 'cover', border: `2px solid ${PRIMARY}`, mb: 1 }}
                        />
                        {editUserData.email && <Typography variant="caption" display="block" color="text.secondary">{editUserData.email}</Typography>}
                        {editUserData.telefono && <Typography variant="caption" display="block" color="text.secondary">{editUserData.telefono}</Typography>}
                        {editUserData.matricula && <Typography variant="caption" display="block" color="text.secondary">Mat: {editUserData.matricula}</Typography>}
                    </Box>
                )}
                {/* Formulario */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flex: 1, maxWidth: 500 }}>
                <TextField
                    label="Usuario"
                    value={usuario}
                    disabled
                    size="small"
                    fullWidth
                    InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                />
                <TextField
                    label="Nombre"
                    value={nombre}
                    onChange={(e) => setEditUserData(prev => ({
                        ...prev,
                        nombre: e.target.value, Nombre: e.target.value
                    }))}
                    size="small"
                    fullWidth
                    InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                />
                <TextField
                    label="Descripcion"
                    value={descripcion}
                    onChange={(e) => setEditUserData(prev => ({
                        ...prev,
                        descripcion: e.target.value, Descripcion: e.target.value
                    }))}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                />
                <FormControl size="small" fullWidth>
                    <InputLabel>Subgrupo</InputLabel>
                    <Select
                        value={String(idSubGrupo)}
                        onChange={(e) => setEditUserData(prev => ({
                            ...prev,
                            idSubGrupo: Number(e.target.value), Id_SubGrupo: Number(e.target.value)
                        }))}
                        label="Subgrupo"
                        sx={{ borderRadius: BORDER_RADIUS }}
                    >
                        {allSubGroups.map(sg => (
                            <MenuItem key={sg.id} value={String(sg.id)}>{titleCase(sg.nombre)}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Id Personal (vinculo)"
                    value={idPersonal}
                    onChange={(e) => setEditUserData(prev => ({
                        ...prev,
                        idPersonal: e.target.value, Id_Personal: e.target.value
                    }))}
                    size="small"
                    fullWidth
                    InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={activo === true || activo === 1}
                            onChange={(e) => setEditUserData(prev => ({
                                ...prev,
                                activo: e.target.checked ? 1 : 0,
                                Activo: e.target.checked ? 1 : 0
                            }))}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: PRIMARY }
                            }}
                        />
                    }
                    label={
                        <Typography variant="body2">
                            {(activo === true || activo === 1) ? 'Activo' : 'Inactivo'}
                        </Typography>
                    }
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={savingUser ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={saveUserData}
                        disabled={savingUser}
                        sx={{
                            bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none',
                            '&:hover': { bgcolor: PRIMARY_DARK }
                        }}
                    >
                        Guardar datos
                    </Button>
                </Box>
                </Box>
            </Box>
        );
    };

    const renderTabPassword = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 400 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Cambiar contraseña del usuario seleccionado
            </Typography>
            <TextField
                label="Nueva contraseña"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                size="small"
                fullWidth
                autoComplete="new-password"
                InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
            />
            <TextField
                label="Confirmar contraseña"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                size="small"
                fullWidth
                autoComplete="new-password"
                error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? 'Las contraseñas no coinciden' : ''}
                InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                    variant="contained"
                    startIcon={savingPassword ? <CircularProgress size={16} color="inherit" /> : <KeyIcon />}
                    onClick={savePassword}
                    disabled={savingPassword || !newPassword || newPassword !== confirmPassword}
                    sx={{
                        bgcolor: PRIMARY, borderRadius: BORDER_RADIUS, textTransform: 'none',
                        '&:hover': { bgcolor: PRIMARY_DARK }
                    }}
                >
                    Cambiar contraseña
                </Button>
            </Box>
        </Box>
    );

    // ═══════════════════════════════════════════════════════
    // MAIN RENDER
    // ═══════════════════════════════════════════════════════
    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', bgcolor: '#f5f5f5' }}>
            {/* ── LEFT PANEL: TREE ── */}
            <Paper
                elevation={0}
                sx={{
                    width: 280, minWidth: 280, display: 'flex', flexDirection: 'column',
                    borderRadius: 0, borderRight: '1px solid #e0e0e0',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <Box sx={{
                    bgcolor: PRIMARY, color: '#fff', px: 2, py: 1.5,
                    display: 'flex', alignItems: 'center', gap: 1
                }}>
                    <SecurityIcon fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Administracion de Permisos
                    </Typography>
                </Box>

                {/* Search */}
                <Box sx={{ px: 1.5, py: 1, bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
                    <TextField
                        placeholder="Buscar usuario..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {searchLoading
                                        ? <CircularProgress size={16} sx={{ color: PRIMARY }} />
                                        : <SearchIcon fontSize="small" sx={{ color: '#999' }} />
                                    }
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => handleSearch('')}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                            sx: { borderRadius: BORDER_RADIUS, fontSize: '0.85rem' }
                        }}
                    />
                    <FormControlLabel
                        control={<Switch size="small" checked={showInactivos} onChange={() => setShowInactivos(v => !v)} />}
                        label={<Typography variant="caption" color="text.secondary">Mostrar inactivos</Typography>}
                        sx={{ mt: 0.5, ml: 0 }}
                    />
                </Box>

                {/* Add group button */}
                <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#fafafa', borderBottom: '1px solid #eee' }}>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openCreateGrupo}
                        sx={{
                            textTransform: 'none', color: PRIMARY, fontSize: '0.8rem',
                            borderRadius: BORDER_RADIUS
                        }}
                    >
                        Nuevo grupo
                    </Button>
                </Box>

                {/* Tree */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {renderTree()}
                </Box>

                {/* Stats */}
                <Box sx={{
                    borderTop: '1px solid #e0e0e0', px: 2, py: 1, bgcolor: '#fafafa',
                    display: 'flex', gap: 2, justifyContent: 'center'
                }}>
                    <Chip
                        icon={<PersonIcon sx={{ fontSize: 14 }} />}
                        label={`${stats.usuarios_activos ?? 0} activos`}
                        size="small"
                        sx={{
                            fontSize: '0.7rem', height: 22,
                            bgcolor: PRIMARY_LIGHT, color: PRIMARY,
                            borderRadius: BORDER_RADIUS
                        }}
                    />
                    <Chip
                        icon={<GroupsIcon sx={{ fontSize: 14 }} />}
                        label={`${stats.grupos ?? 0} grupos`}
                        size="small"
                        sx={{
                            fontSize: '0.7rem', height: 22,
                            bgcolor: '#e8eaf6', color: '#3949ab',
                            borderRadius: BORDER_RADIUS
                        }}
                    />
                </Box>
            </Paper>

            {/* ── RIGHT PANEL: DETAIL ── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {!selectedUserId ? (
                    <Box sx={{
                        flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center',
                        flexDirection: 'column', gap: 2, color: '#bbb'
                    }}>
                        <AccountTreeIcon sx={{ fontSize: 72, color: '#ddd' }} />
                        <Typography variant="body1" color="text.secondary">
                            Seleccione un usuario del panel izquierdo
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* User header */}
                        <Box sx={{
                            bgcolor: '#fff', px: 3, py: 1.5,
                            borderBottom: '1px solid #e0e0e0',
                            display: 'flex', alignItems: 'center', gap: 2
                        }}>
                            {editUserData?.idPersonal ? (
                                <Box component="img"
                                    src={`${nodeApiPath('permisos/foto-personal/' + editUserData.idPersonal)}?token=${localStorage.getItem('SIGIF_JWT_TOKEN')}`}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                    sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${PRIMARY}` }}
                                />
                            ) : (
                                <PersonIcon sx={{ color: PRIMARY, fontSize: 28 }} />
                            )}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                    {titleCase(selectedUserName)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    ID: {selectedUserId}
                                </Typography>
                            </Box>
                            <Tooltip title="Copiar permisos de otro usuario">
                                <IconButton size="small" onClick={() => { setCopiarDialog({ open: true }); setCopiarOrigen(''); setCopiarSearchResults([]); }}
                                    sx={{ color: PRIMARY }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Exportar permisos">
                                <IconButton size="small" onClick={handleExportarPermisos} sx={{ color: '#2e7d32' }}>
                                    <SaveIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={() => { setSelectedUserId(null); setSelectedUserName(''); }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Tabs */}
                        <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e0e0e0' }}>
                            <Tabs
                                value={activeTab}
                                onChange={(_, v) => setActiveTab(v)}
                                sx={{
                                    minHeight: 40,
                                    '& .MuiTab-root': {
                                        minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem'
                                    },
                                    '& .Mui-selected': { color: PRIMARY },
                                    '& .MuiTabs-indicator': { bgcolor: PRIMARY }
                                }}
                            >
                                <Tab icon={<InfoIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Datos" />
                                <Tab icon={<SecurityIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Permisos" />
                                <Tab icon={<KeyIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Contraseña" />
                            </Tabs>
                        </Box>

                        {/* Tab content */}
                        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                            {activeTab === 0 && renderTabDatos()}
                            {activeTab === 1 && renderTabPermisos()}
                            {activeTab === 2 && renderTabPassword()}
                        </Box>
                    </>
                )}
            </Box>

            {/* ── CONTEXT MENU ── */}
            <Menu
                open={contextMenu !== null}
                onClose={closeContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
                PaperProps={{ sx: { borderRadius: BORDER_RADIUS, minWidth: 180 } }}
            >
                {contextTarget?.type === 'grupo' && [
                    <MenuItem key="edit-g" onClick={openEditGrupo} sx={{ fontSize: '0.85rem' }}>
                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                        Editar grupo
                    </MenuItem>,
                    <MenuItem key="add-sg" onClick={openCreateSubGrupo} sx={{ fontSize: '0.85rem' }}>
                        <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                        Agregar subgrupo
                    </MenuItem>,
                    <Divider key="d1" />,
                    <MenuItem key="del-g" onClick={openDeleteGrupo} sx={{ fontSize: '0.85rem', color: '#d32f2f' }}>
                        <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
                        Eliminar grupo
                    </MenuItem>,
                ]}
                {contextTarget?.type === 'subgrupo' && [
                    <MenuItem key="edit-sg" onClick={openEditSubGrupo} sx={{ fontSize: '0.85rem' }}>
                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                        Editar subgrupo
                    </MenuItem>,
                    <MenuItem key="add-u" onClick={openCreateUsuario} sx={{ fontSize: '0.85rem' }}>
                        <ListItemIcon><PersonAddIcon fontSize="small" /></ListItemIcon>
                        Agregar usuario
                    </MenuItem>,
                    <MenuItem key="bulk" onClick={openBulkAssign} sx={{ fontSize: '0.85rem' }}>
                        <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                        Asignar permisos masivo
                    </MenuItem>,
                    <Divider key="d2" />,
                    <MenuItem key="del-sg" onClick={openDeleteGrupo} sx={{ fontSize: '0.85rem', color: '#d32f2f' }}>
                        <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
                        Eliminar subgrupo
                    </MenuItem>,
                ]}
            </Menu>

            {/* ── DIALOG: CREATE/EDIT GROUP ── */}
            <ColorDialog
                open={dialogGrupo.open}
                onClose={() => setDialogGrupo(prev => ({ ...prev, open: false }))}
                title={dialogGrupo.mode === 'create' ? 'Nuevo grupo' : 'Editar grupo'}
                icon={<GroupsIcon fontSize="small" />}
                actions={
                    <>
                        <Button onClick={() => setDialogGrupo(prev => ({ ...prev, open: false }))}
                            sx={{ textTransform: 'none', borderRadius: BORDER_RADIUS }}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={saveGrupo}
                            sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                            {dialogGrupo.mode === 'create' ? 'Crear' : 'Guardar'}
                        </Button>
                    </>
                }
            >
                <TextField
                    label="Nombre del grupo"
                    value={dialogGrupo.nombre}
                    onChange={(e) => setDialogGrupo(prev => ({ ...prev, nombre: e.target.value }))}
                    size="small"
                    fullWidth
                    autoFocus
                    sx={{ mt: 1 }}
                    InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveGrupo(); }}
                />
            </ColorDialog>

            {/* ── DIALOG: CREATE/EDIT SUBGROUP ── */}
            <ColorDialog
                open={dialogSubGrupo.open}
                onClose={() => setDialogSubGrupo(prev => ({ ...prev, open: false }))}
                title={dialogSubGrupo.mode === 'create' ? 'Nuevo subgrupo' : 'Editar subgrupo'}
                icon={<GroupIcon fontSize="small" />}
                actions={
                    <>
                        <Button onClick={() => setDialogSubGrupo(prev => ({ ...prev, open: false }))}
                            sx={{ textTransform: 'none', borderRadius: BORDER_RADIUS }}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={saveSubGrupo}
                            sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                            {dialogSubGrupo.mode === 'create' ? 'Crear' : 'Guardar'}
                        </Button>
                    </>
                }
            >
                <TextField
                    label="Nombre del subgrupo"
                    value={dialogSubGrupo.nombre}
                    onChange={(e) => setDialogSubGrupo(prev => ({ ...prev, nombre: e.target.value }))}
                    size="small"
                    fullWidth
                    autoFocus
                    sx={{ mt: 1 }}
                    InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveSubGrupo(); }}
                />
            </ColorDialog>

            {/* ── DIALOG: DELETE CONFIRM ── */}
            <ColorDialog
                open={dialogDeleteGrupo.open}
                onClose={() => setDialogDeleteGrupo(prev => ({ ...prev, open: false }))}
                title={`Eliminar ${dialogDeleteGrupo.type === 'grupo' ? 'grupo' : 'subgrupo'}`}
                icon={<DeleteIcon fontSize="small" />}
                actions={
                    <>
                        <Button onClick={() => setDialogDeleteGrupo(prev => ({ ...prev, open: false }))}
                            sx={{ textTransform: 'none', borderRadius: BORDER_RADIUS }}>
                            Cancelar
                        </Button>
                        <Button variant="contained" color="error" onClick={confirmDelete}
                            sx={{ textTransform: 'none', borderRadius: BORDER_RADIUS }}>
                            Eliminar
                        </Button>
                    </>
                }
            >
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Esta seguro de que desea eliminar {dialogDeleteGrupo.type === 'grupo' ? 'el grupo' : 'el subgrupo'}{' '}
                    <strong>{titleCase(dialogDeleteGrupo.nombre)}</strong>?
                    {dialogDeleteGrupo.type === 'grupo' && (
                        <Typography variant="caption" display="block" color="error" sx={{ mt: 1 }}>
                            Esto eliminara todos los subgrupos y desvinculara los usuarios asociados.
                        </Typography>
                    )}
                </Typography>
            </ColorDialog>

            {/* ── DIALOG: CREATE USER ── */}
            <ColorDialog
                open={dialogUsuario.open}
                onClose={() => setDialogUsuario({ open: false, idSubGrupo: null, subGrupoNombre: '' })}
                title="Nuevo usuario"
                icon={<PersonAddIcon fontSize="small" />}
                actions={
                    <>
                        <Button onClick={() => setDialogUsuario({ open: false, idSubGrupo: null, subGrupoNombre: '' })}
                            sx={{ textTransform: 'none', borderRadius: BORDER_RADIUS }}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={saveNuevoUsuario}
                            sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                            Crear usuario
                        </Button>
                    </>
                }
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Subgrupo: <strong>{titleCase(dialogUsuario.subGrupoNombre)}</strong>
                    </Typography>
                    <TextField
                        label="Usuario (login)"
                        value={nuevoUsuario.usuario}
                        onChange={(e) => setNuevoUsuario(prev => ({ ...prev, usuario: e.target.value }))}
                        size="small"
                        fullWidth
                        autoFocus
                        InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                    />
                    <TextField
                        label="Nombre completo"
                        value={nuevoUsuario.nombre}
                        onChange={(e) => setNuevoUsuario(prev => ({ ...prev, nombre: e.target.value }))}
                        size="small"
                        fullWidth
                        InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                    />
                    <TextField
                        label="Descripcion (opcional)"
                        value={nuevoUsuario.descripcion}
                        onChange={(e) => setNuevoUsuario(prev => ({ ...prev, descripcion: e.target.value }))}
                        size="small"
                        fullWidth
                        InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                    />
                    <TextField
                        label="Contraseña"
                        type="password"
                        value={nuevoUsuario.password}
                        onChange={(e) => setNuevoUsuario(prev => ({ ...prev, password: e.target.value }))}
                        size="small"
                        fullWidth
                        autoComplete="new-password"
                        InputProps={{ sx: { borderRadius: BORDER_RADIUS } }}
                    />
                </Box>
            </ColorDialog>

            {/* ── DIALOG: BULK ASSIGN ── */}
            <ColorDialog
                open={dialogMasivo.open}
                onClose={() => setDialogMasivo({ open: false, idSubGrupo: null, subGrupoNombre: '' })}
                title="Asignar permisos masivamente"
                icon={<ContentCopyIcon fontSize="small" />}
                maxWidth="md"
                actions={
                    <>
                        <Button onClick={() => setDialogMasivo({ open: false, idSubGrupo: null, subGrupoNombre: '' })}
                            sx={{ textTransform: 'none', borderRadius: BORDER_RADIUS }}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={saveMasivo}
                            disabled={!masivoModuloId}
                            sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BORDER_RADIUS, '&:hover': { bgcolor: PRIMARY_DARK } }}
                        >
                            Aplicar a todo el subgrupo
                        </Button>
                    </>
                }
            >
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Asignar permisos a <strong>todos los usuarios</strong> del subgrupo{' '}
                        <Chip label={titleCase(dialogMasivo.subGrupoNombre)} size="small"
                            sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 600, borderRadius: BORDER_RADIUS }} />
                    </Typography>
                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Modulo</InputLabel>
                        <Select
                            value={masivoModuloId}
                            onChange={(e) => setMasivoModuloId(e.target.value)}
                            label="Modulo"
                            sx={{ borderRadius: BORDER_RADIUS }}
                        >
                            <MenuItem value=""><em>Seleccione un modulo</em></MenuItem>
                            {modulos.map(m => (
                                <MenuItem key={m.Id || m.id} value={String(m.Id || m.id)}>
                                    {titleCase(m.Nombre || m.nombre)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {masivoModuloId && renderPermisosMatrix(masivoMatrix, handleMasivoChange)}
                    {!masivoModuloId && (
                        <Box sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                            <Typography variant="body2">Seleccione un modulo para configurar permisos</Typography>
                        </Box>
                    )}
                </Box>
            </ColorDialog>

            {/* ── SNACKBAR ── */}
            {/* Dialog Copiar Permisos */}
            <Dialog open={copiarDialog.open} onClose={() => setCopiarDialog({ open: false })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: PRIMARY, color: 'white' }}>
                    Copiar permisos de otro usuario
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Se copiarán TODOS los permisos del usuario seleccionado a <strong>{titleCase(selectedUserName)}</strong>.
                        Los permisos actuales serán reemplazados.
                    </Typography>
                    <Autocomplete
                        options={copiarSearchResults}
                        getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.Id} - ${titleCase(opt.Apellido_Nombre || '')}`}
                        filterOptions={(x) => x}
                        onInputChange={(e, val) => handleCopiarSearch(val)}
                        onChange={(e, val) => { if (val) setCopiarOrigen(val.Id); }}
                        renderOption={(props, opt) => {
                            const { key, ...rest } = props;
                            return <li key={opt.Id} {...rest}><Typography variant="body2">{opt.Id} - {titleCase(opt.Apellido_Nombre || '')}</Typography></li>;
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Buscar usuario origen" size="small" fullWidth
                                helperText="Buscar por nombre o usuario" />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCopiarDialog({ open: false })} color="inherit">Cancelar</Button>
                    <Button onClick={handleCopiarPermisos} variant="contained" disabled={!copiarOrigen}
                        sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                        Copiar Permisos
                    </Button>
                </DialogActions>
            </Dialog>

            <SnackbarNotif
                open={snack.open}
                msg={snack.msg}
                severity={snack.severity}
                onClose={() => setSnack(prev => ({ ...prev, open: false }))}
            />
        </Box>
    );
}
